
import { Component, Mixins, Prop } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import HealthHistory from "../system-view/HealthHistory.vue";
import ObjectTypeId from "@/types/sv-data/enums/ObjectTypeIds";
import BadgeActivity from "@/components/BadgeActivity.vue";
import ControlDeviceItem from "@/components/media/ControlDeviceItem.vue";
import { UserPermissions } from "@/store/types";
import { ResponseBadgeActivityDetails } from "@/store/badge-activity/types";
import BadgeActivityApi from "@/services/api.badgeActivity.service";
import { DeviceControllerOutput, defaultDeviceControllerOutput } from "@/store/site-monitor-cameras/types";
import { EventDetails } from "@/store/site-monitor/types";
import RelayCapabilitiesMixin from "@/mixins/RelayCapabilitiesMixin";
import api from "@/services/api.service";

const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		"health-history": HealthHistory,
		"badge-activity": BadgeActivity,
		"control-device-item": ControlDeviceItem
	}
})
export default class MarkerInfoWindowDetails extends Mixins(RelayCapabilitiesMixin) {
	@Prop({ type: String, required: true })
    private readonly deviceTitle: string;

	@Prop({ required: true, default: null })
	private readonly objectId : number | null;

	@Prop({ required: true, default: null })
	private readonly objectTypeId : ObjectTypeId | null;

	@Prop({ type: Boolean, default: false })
	private readonly allowDeviceControl: boolean;

	@Getter getFeature: (featureName: string[]) => boolean;
	@Getter getPermissions: UserPermissions;
	@SiteMonitor.Getter("getEventDetails") getEventDetails: EventDetails | null;

	private badgeActivitySupported: ResponseBadgeActivityDetails | null = null;
	private controlOutput: DeviceControllerOutput | null = null;

	public async mounted(): Promise<void> {
		await this.checkBadgeActivitySupport();
		await this.loadControlDevice();
	}

	/**
	 *@summary feature and permission check for enabling Health History tab.
	 */
	private get showHealthHistoryTab(): boolean {
		return this.getFeature(["Map", "PopOver", "DeviceStatus"]) &&
			this.getPermissions.canViewSystemView;
	}

	private get showBadgeActivityTab(): boolean {
		return this.isBadgeActivityEnabled &&
			this.objectTypeId === ObjectTypeId.Alarm &&
			this.badgeActivitySupported &&
			this.badgeActivitySupported?.canBadgeActivity;
	}

	private get isBadgeActivityEnabled(): boolean {
		return this.getFeature(["Map", "PopOver", "BadgeActivity"]) &&
			!!this.getPermissions.canViewBadgeActivity;
	}

	private get hasActiveEvent(): boolean {
		return !!this.getEventDetails && this.getEventDetails.eventID > 0;
	}

	/**
	 *@summary feature, permission and type check for enabling the Control tab.
	 */
	private get showControlTab(): boolean {
		return this.allowDeviceControl &&
			this.hasActiveEvent &&
			this.getFeature(["Map", "PopOver", "Control"]) &&
			!!this.getPermissions.canViewMediaMatrixControls &&
			(this.objectTypeId === ObjectTypeId.Door || this.objectTypeId === ObjectTypeId.Relay);
	}

	private get showControlDeviceTab(): boolean {
		return this.showControlTab && !!this.controlOutput && (this.controlOutput.canPulse || this.controlOutput.canOnOff);
	}

	private get shouldShowAnyTabs(): boolean {
		return this.showHealthHistoryTab || this.showBadgeActivityTab || this.showControlDeviceTab;
	}

	private async checkBadgeActivitySupport(): Promise<void> {
		if (this.isBadgeActivityEnabled && this.objectTypeId === ObjectTypeId.Alarm) {
			this.badgeActivitySupported = await BadgeActivityApi.badgeActivitySupported(this.objectId);
		}
	}

	private async loadControlDevice(): Promise<void> {
		if (!this.showControlTab) {
			return;
		}

		const output: DeviceControllerOutput = {
			...defaultDeviceControllerOutput(),
			title: this.deviceTitle,
			objectId: this.objectId
		};

		try {
			const devices = await api.getDevicesByIds([this.objectId]);
			const device = Array.isArray(devices)
				? devices.find(d => d.deviceID === this.objectId)
				: null;

			if (device && device.settingsMeta) {
				const { isOutput, canPulse, canOnOff } = this.getRelayCapabilities(device.settingsMeta);
				output.isOutput = isOutput;
				output.canPulse = canPulse;
				output.canOnOff = canOnOff;
			}

			output.controlRequest = true;
			this.controlOutput = output;
		} catch (error) {
			// Leave the Control tab hidden if we can't determine the device's capabilities.
			console.error("Failed to load device capabilities for the map control tab:", error);
		}
	}
}
